// Mart (sari-sari staples) rules for PopCart: pure and shared by the API (authoritative), the
// customer app (display) and the pick list. Integer centavos only. Contract: docs/PHASE5-CONTRACT.md.
//
// Substitution policy: the customer never pays more. A substitute is charged at the LOWER of its
// own price and the original unit price; anything not found or not replaced is not charged.

/** Natural shelf order: "A2" < "A10" < "B1"; blank shelves last; ties by name. */
export function shelfCompare(a, b) {
  const sa = (a.shelf || '').trim().toUpperCase();
  const sb = (b.shelf || '').trim().toUpperCase();
  if (!sa !== !sb) return sa ? -1 : 1;
  const c = sa.localeCompare(sb, 'en', { numeric: true });
  return c || String(a.name || '').localeCompare(String(b.name || ''), 'en');
}

/** The pick list for one shop order: lines sorted by shelf, then name. */
export function sortPickList(lines) {
  return [...lines].sort(shelfCompare);
}

/** What one substitute unit costs the customer. */
export function substituteUnitCentavos(originalUnitCentavos, substituteUnitPrice) {
  if (!(originalUnitCentavos >= 0) || !(substituteUnitPrice >= 0)) throw new RangeError('prices must be ≥ 0');
  return Math.min(originalUnitCentavos, substituteUnitPrice);
}

/**
 * Settle one picked line.
 * @param {{ unitCentavos: number, qty: number, pickedQty: number, substitute?: { unitCentavos: number, qty: number } | null }} l
 *   unitCentavos: what the customer agreed to per unit; substitute.unitCentavos: the substitute's own price.
 * @returns {{ status: 'picked'|'short'|'substituted', finalLineCentavos: number, deltaCentavos: number, substituteUnitCentavos: number|null }}
 *   deltaCentavos = final − agreed (≤ 0: the customer never pays more).
 */
export function settlePick({ unitCentavos, qty, pickedQty, substitute = null }) {
  if (!Number.isInteger(qty) || qty < 1) throw new RangeError('qty must be a positive integer');
  if (!Number.isInteger(pickedQty) || pickedQty < 0 || pickedQty > qty) throw new RangeError('pickedQty must be 0..qty');
  const missing = qty - pickedQty;
  let subQty = 0;
  let subUnit = null;
  if (substitute && missing > 0) {
    if (!Number.isInteger(substitute.qty) || substitute.qty < 0 || substitute.qty > missing) throw new RangeError('substitute qty must be 0..missing');
    subQty = substitute.qty;
    subUnit = substituteUnitCentavos(unitCentavos, substitute.unitCentavos);
  }
  const finalLine = pickedQty * unitCentavos + subQty * (subUnit ?? 0);
  const status = missing === 0 ? 'picked' : subQty > 0 ? 'substituted' : 'short';
  return { status, finalLineCentavos: finalLine, deltaCentavos: finalLine - qty * unitCentavos, substituteUnitCentavos: subQty > 0 ? subUnit : null };
}

/** A value pack's savings against buying its parts: max(0, Σ part price × qty − pack price). */
export function packSavings(packPriceCentavos, parts) {
  const sum = parts.reduce((t, p) => t + p.priceCentavos * p.qty, 0);
  return Math.max(0, sum - packPriceCentavos);
}

/** "Only 3 left" badge: 0 < available ≤ threshold. */
export function isLowStock(available, threshold = 5) {
  return available > 0 && available <= threshold;
}

/** How much more the customer must add to reach a shop's minimum (0 when met). */
export function belowMinimumBy(subtotalCentavos, minOrderCentavos) {
  return Math.max(0, (minOrderCentavos || 0) - subtotalCentavos);
}

/**
 * A line's substitution preference: explicit per-line choice wins; otherwise the order-level
 * "swap for something similar" flag (Phase 2) maps to 'similar' / 'none'.
 */
export function effectiveSubMode(line, orderSubstitutionOk = true) {
  if (line && (line.mode === 'similar' || line.mode === 'product' || line.mode === 'none')) return line.mode;
  return orderSubstitutionOk ? 'similar' : 'none';
}
